import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Stethoscope, ChevronLeft } from "lucide-react";

const SPECS = [
  { v: "psychiatrist", l: "Psychiatrist" },
  { v: "neuro_cognitive", l: "Neurologist / Cognitive specialist" },
  { v: "adjunctive", l: "Adjunctive therapist" },
];
const LANGS = ["en", "hi", "ta", "te", "kn", "mr", "bn"];

export default function Apply() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    specialization: "psychiatrist",
    credentials: "",
    affiliation: "",
    license_number: "",
    session_fee_inr: 799,
    languages: ["en"],
    bio: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleLang = (code) =>
    set("languages", form.languages.includes(code) ? form.languages.filter((c) => c !== code) : [...form.languages, code]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/specialists/apply", form);
      toast.success("Application submitted — you'll be able to sign in once approved.");
      nav("/login");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--mano-bg)] py-8 px-5" data-testid="apply-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" data-testid="apply-back-home" className="uppercase-label inline-flex items-center gap-1">
          <ChevronLeft size={14} /> Home
        </Link>

        <div className="bg-white rounded-3xl border border-[color:var(--mano-border)] p-7 lg:p-9 mt-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-[color:var(--mano-primary)] text-white flex items-center justify-center">
              <Stethoscope size={20} />
            </div>
            <div>
              <span className="uppercase-label">Specialist application</span>
              <h1 className="font-display text-3xl">Join Manodayam</h1>
            </div>
          </div>

          <p className="text-[color:var(--mano-ink-soft)] mt-3">
            Share your credentials. Our team will verify and approve you — you'll be notified via email.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Full name"><input required data-testid="apply-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} /></Field>
              <Field label="Work email"><input required type="email" data-testid="apply-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Mobile number">
                <input
                  required
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  minLength={7}
                  placeholder="+91 98765 43210"
                  data-testid="apply-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className={inp}
                />
              </Field>
              <Field label="Choose a password"><input required type="password" data-testid="apply-password" value={form.password} onChange={(e) => set("password", e.target.value)} className={inp} /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Specialisation">
                <select data-testid="apply-specialization" value={form.specialization} onChange={(e) => set("specialization", e.target.value)} className={inp}>
                  {SPECS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </Field>
              <Field label="Session fee (INR)"><input type="number" min="0" data-testid="apply-fee" value={form.session_fee_inr} onChange={(e) => set("session_fee_inr", Number(e.target.value))} className={inp} /></Field>
            </div>
            <Field label="Credentials (degrees, licenses, years of experience)">
              <textarea required data-testid="apply-credentials" value={form.credentials} onChange={(e) => set("credentials", e.target.value)} className={`${inp} !h-24 py-3`} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Affiliation / hospital"><input data-testid="apply-affiliation" value={form.affiliation} onChange={(e) => set("affiliation", e.target.value)} className={inp} /></Field>
              <Field label="License / registration number"><input data-testid="apply-license" value={form.license_number} onChange={(e) => set("license_number", e.target.value)} className={inp} /></Field>
            </div>
            <Field label="Languages you can consult in">
              <div className="flex flex-wrap gap-1.5 pt-1">
                {LANGS.map((c) => (
                  <button type="button" key={c} onClick={() => toggleLang(c)} data-testid={`apply-lang-${c}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      form.languages.includes(c)
                        ? "bg-[color:var(--mano-primary)] text-white border-[color:var(--mano-primary)]"
                        : "border-[color:var(--mano-border)]"
                    }`}
                  >
                    {c.toUpperCase()}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Short bio (visible to patients)">
              <textarea data-testid="apply-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} className={`${inp} !h-24 py-3`} />
            </Field>
            <button
              data-testid="apply-submit"
              type="submit"
              disabled={busy}
              className="w-full h-12 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold transition-colors disabled:opacity-60"
            >
              {busy ? "Submitting…" : "Submit application"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const inp = "mt-2 w-full h-12 px-4 rounded-xl border border-[color:var(--mano-border)] bg-[color:var(--mano-bg)] focus:outline-none focus:border-[color:var(--mano-primary)]";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="uppercase-label">{label}</span>
      {children}
    </label>
  );
}
